/**
 * Activity Tracker Service
 *
 * Generic event tracking service for any activity trail in the application. Tracks user actions as `ActivityEvent`
 * entries for any session type — impersonation (simulation), Nurse Practitioner, or any future role.
 *
 * ## Lifecycle (same for every session type)
 *
 * 1. `startSession(userId, userName, type)` — initialises a new tracked session
 * 2. `recordEvent(event)` — appends a single event to the durable buffer
 * 3. `endSession()` — drains + tears down the session entirely
 * 4. `closeSession()` — tears down the session marker without draining (offline path)
 * 5. On next login: `recoverOrphanedSessions()` — finds and returns any events from sessions that ended without a flush
 *
 * ## Storage design
 *
 * All state is persisted in `localStorage` (synchronous, durable, survives tab/browser close). `sessionStorage` is
 * intentionally NOT used — it is cleared when the tab closes, which would silently discard events before they can be
 * flushed (critical for offline scenarios).
 *
 * Cross-MFE consistency: each MFE remote runs in its own Angular injector, so multiple instances of this class exist
 * simultaneously. All instances read and write the same localStorage keys — a `startSession()` call from web.admin is
 * immediately visible to the shell, and a `flushEvents()` in web.shell collects events recorded by web.assessment.
 *
 * | Key                      | Value                                       |
 * | ------------------------ | ------------------------------------------- |
 * | `act_session`            | `ActivitySession` — active session metadata |
 * | `act_events_{sessionId}` | `ActivityEvent[]` — universal event buffer  |
 *
 * @packageDocumentation
 */

import { Injectable } from '@angular/core';
import type { ActivityLogDetail } from '../models/activity-log.model';

// ─── Public types ─────────────────────────────────────────────────────────────

/**
 * Discriminates between tracked session types. Callers use this to conditionally include type-specific fields (e.g.
 * `eventTypeCode` for NP entries) when building an `ActivityEventInput`.
 */
export type TrackerSessionType = 'simulation' | 'np-activity';

/**
 * A fully-resolved activity event as stored in localStorage and returned by `flushEvents()` / `endSession()` /
 * `recoverOrphanedSessions()`.
 *
 * `sequenceNo` and `recordedAt` are set automatically by `recordEvent()`.
 */
export interface ActivityEvent {
  /** Short, human-readable action label / step name */
  stepName: string;
  /** Longer description of what happened */
  stepDescription: string;
  /** Outcome of the action */
  status: 'Success' | 'Failed' | 'InProgress';
  /**
   * DB event type code (ActivityEventType table). Required for NP individual POSTs; omitted for simulation
   * activityDetails steps. Examples: 'VIEW', 'UPDATE', 'DELETE', 'EDIT', 'ARCHIVE'
   */
  eventTypeCode?: string;
  /** Module / feature area where the action occurred (e.g. 'Assessment', 'Admin') */
  moduleName?: string;
  /** Type of entity being acted upon (e.g. 'NursePractitioner', 'Assessment') */
  entityTypeName?: string;
  /** Optional identifier of the affected entity */
  entityId?: string;
  /** Optional JSON-serialised request context (route, entity IDs, mode…) */
  requestPayload?: string | null;
  /** Optional JSON-serialised response or outcome data */
  responsePayload?: string | null;
  /** Non-null when status === 'Failed' */
  errorMessage?: string | null;
  /** 1-based sequence number within this session, auto-set by `recordEvent()` */
  sequenceNo: number;
  /** ISO-8601 timestamp when the event was recorded, auto-set by `recordEvent()` */
  recordedAt: string;
}

/**
 * Input shape for `recordEvent()`. Omits the two auto-set fields so callers never need to supply `sequenceNo` or
 * `recordedAt`.
 */
export type ActivityEventInput = Omit<ActivityEvent, 'sequenceNo' | 'recordedAt'>;

/**
 * One orphaned session returned by `recoverOrphanedSessions()` — a session whose events were never flushed (browser
 * closed offline, tab crash, etc.).
 */
export interface OrphanedSession {
  /** The sessionId that namespaces this session's localStorage key */
  sessionId: string;
  /** Session metadata (type, userId, userName, startedAt) */
  type: TrackerSessionType;
  userId: string;
  userName: string;
  /** All buffered events from this session */
  events: ActivityEvent[];
}

/** @internal Metadata stored under `act_session` */
interface ActivitySession {
  type: TrackerSessionType;
  sessionId: string;
  userId: string;
  userName: string;
  startedAt: string;
}

// ─── Service ──────────────────────────────────────────────────────────────────

/**
 * Generic activity tracking service shared across all Module Federation remotes.
 *
 * Use `recordEvent(event)` to append any action. Use `flushEvents()` for periodic batched POSTs (session stays active).
 * Use `endSession()` when the session is closing (online). Use `closeSession()` when closing offline.
 */
@Injectable({
  providedIn: 'root',
})
export class ActivityTrackerService {
  private static readonly KEY_SESSION = 'act_session';
  private static readonly KEY_EVENTS_PREFIX = 'act_events_';

  // ── Getters ──────────────────────────────────────────────────────────────

  /** True when any tracking session is currently active */
  get isSessionActive(): boolean {
    return this.readSession() !== null;
  }

  /** The active session type, or null when no session is active */
  get sessionType(): TrackerSessionType | null {
    return this.readSession()?.type ?? null;
  }

  /** The tracked user's ID, or null when no session is active */
  get sessionUserId(): string | null {
    return this.readSession()?.userId ?? null;
  }

  /** The tracked user's display name, or null when no session is active */
  get sessionUserName(): string | null {
    return this.readSession()?.userName ?? null;
  }

  // ── Session lifecycle ────────────────────────────────────────────────────

  /**
   * Begin a new tracking session. Generates a unique `sessionId` used as the localStorage event-buffer key so that
   * concurrent or orphaned sessions never collide.
   *
   * @param userId - ID of the user being tracked
   * @param userName - Display name of the user being tracked
   * @param type - Session type; defaults to `'simulation'`
   */
  startSession(userId: string, userName: string, type: TrackerSessionType = 'simulation'): void {
    const sessionId = this.generateSessionId();
    const session: ActivitySession = { type, sessionId, userId, userName, startedAt: new Date().toISOString() };
    localStorage.setItem(ActivityTrackerService.KEY_SESSION, JSON.stringify(session));
    localStorage.setItem(this.eventsKey(sessionId), '[]');
  }

  // ── Core event API ───────────────────────────────────────────────────────

  /**
   * Append a single event to the active session's durable buffer. No-op when no session is active.
   *
   * `sequenceNo` (1-based, scoped to this session) and `recordedAt` (ISO-8601) are set automatically.
   *
   * @param event - The activity input; omit `sequenceNo` and `recordedAt`
   */
  recordEvent(event: ActivityEventInput): void {
    const session = this.readSession();
    if (!session) {
      return;
    }

    const existing = this.readEvents(session.sessionId);
    const resolved: ActivityEvent = {
      ...event,
      sequenceNo: existing.length + 1,
      recordedAt: new Date().toISOString(),
    };

    localStorage.setItem(this.eventsKey(session.sessionId), JSON.stringify([...existing, resolved]));
  }

  /**
   * Drain the event buffer and return all events. Resets the buffer to `[]` but keeps the session active. Use for
   * periodic flushes (e.g. every 5 minutes).
   *
   * @returns The drained events, or `[]` when no session is active.
   */
  flushEvents(): ActivityEvent[] {
    const session = this.readSession();
    if (!session) {
      return [];
    }

    const snapshot = this.readEvents(session.sessionId);
    localStorage.setItem(this.eventsKey(session.sessionId), '[]');
    return snapshot;
  }

  /**
   * End the session: drain the remaining events and clear all session keys from localStorage. Use when the session is
   * closing and the network is available.
   *
   * @returns The final set of events to be POSTed before the session is destroyed.
   */
  endSession(): ActivityEvent[] {
    const session = this.readSession();
    if (!session) {
      return [];
    }

    const snapshot = this.readEvents(session.sessionId);
    localStorage.removeItem(this.eventsKey(session.sessionId));
    localStorage.removeItem(ActivityTrackerService.KEY_SESSION);
    return snapshot;
  }

  /**
   * Close the session without draining the event buffer. Use when the device is offline at logout — the buffer remains
   * in localStorage under its session-scoped key and will be discovered by `recoverOrphanedSessions()` on the next
   * login.
   */
  closeSession(): void {
    localStorage.removeItem(ActivityTrackerService.KEY_SESSION);
  }

  /** Returns a read-only snapshot of buffered activity-log detail rows without modifying them. */
  peekActivityLogDetails(storageKey: string): readonly ActivityLogDetail[] {
    return this.readActivityLogDetails(this.resolveActivityLogStorageKey(storageKey));
  }

  /** Append a single activity-log detail row to the shared local buffer. */
  recordActivityLogDetail(storageKey: string, detail: ActivityLogDetail): void {
    const resolvedStorageKey = this.resolveActivityLogStorageKey(storageKey);
    const existing = this.readActivityLogDetails(resolvedStorageKey);
    const now = new Date().toISOString();
    const resolved: ActivityLogDetail = {
      ...detail,
      sequenceNo: existing.length + 1,
      startTime: detail.startTime ?? now,
      endTime: detail.endTime ?? now,
      durationMs: detail.durationMs ?? 0,
    };

    this.writeActivityLogDetails(resolvedStorageKey, [...existing, resolved]);
  }

  /**
   * Append a detail row using the common template-builder style input. This keeps the caller side simple while the
   * service fills in timing defaults.
   */
  recordActivityLogAction(
    storageKey: string,
    stepName: string,
    stepDescription: string,
    status: ActivityLogDetail['status'] = 'Success',
    detail?: Partial<ActivityLogDetail>
  ): void {
    this.recordActivityLogDetail(storageKey, {
      stepName,
      stepDescription,
      status,
      errorMessage: detail?.errorMessage ?? null,
      requestPayload: detail?.requestPayload ?? null,
      responsePayload: detail?.responsePayload ?? null,
      sequenceNo: detail?.sequenceNo ?? 0,
      startTime: detail?.startTime ?? new Date().toISOString(),
      endTime: detail?.endTime ?? new Date().toISOString(),
      durationMs: detail?.durationMs ?? 0,
    });
  }

  /** Backward-compatible alias for the former template buffer service API. */
  peek(storageKey: string): readonly ActivityLogDetail[] {
    return this.peekActivityLogDetails(storageKey);
  }

  /** Backward-compatible alias for the former template buffer service API. */
  record(storageKey: string, detail: ActivityLogDetail): void {
    this.recordActivityLogDetail(storageKey, detail);
  }

  /** Backward-compatible alias for the former template buffer service API. */
  recordAction(
    storageKey: string,
    stepName: string,
    stepDescription: string,
    status: ActivityLogDetail['status'] = 'Success',
    detail?: Partial<ActivityLogDetail>
  ): void {
    this.recordActivityLogAction(storageKey, stepName, stepDescription, status, detail);
  }

  /** Backward-compatible alias for the former template buffer service API. */
  clear(storageKey: string): void {
    this.clearActivityLogDetails(storageKey);
  }

  /** Backward-compatible alias for the former template buffer service API. */
  drain(storageKey: string): ActivityLogDetail[] {
    return this.drainActivityLogDetails(storageKey);
  }

  /** Clear all buffered activity-log detail rows. */
  clearActivityLogDetails(storageKey: string): void {
    localStorage.removeItem(this.resolveActivityLogStorageKey(storageKey));
  }

  /** Drain the buffered detail rows and clear the local buffer. */
  drainActivityLogDetails(storageKey: string): ActivityLogDetail[] {
    const resolvedStorageKey = this.resolveActivityLogStorageKey(storageKey);
    const details = this.readActivityLogDetails(resolvedStorageKey);
    this.clearActivityLogDetails(storageKey);
    return details;
  }

  private resolveActivityLogStorageKey(storageKey: string): string {
    if (storageKey === 'template-designer') {
      return 'template_activity_detail_buffer';
    }

    return storageKey;
  }

  /** Returns a read-only snapshot of the current event buffer without modifying it. */
  peekEvents(): readonly ActivityEvent[] {
    const session = this.readSession();
    if (!session) {
      return [];
    }
    return this.readEvents(session.sessionId);
  }

  /**
   * Scan localStorage for event buffers from sessions that were never flushed (browser closed offline, tab crash,
   * etc.). Returns those orphaned sessions and removes their localStorage keys.
   *
   * **Call this before `startSession()`** so the newly created session's key is never misidentified as an orphan.
   *
   * @returns An array of orphaned sessions, each with its metadata and events.
   */
  recoverOrphanedSessions(): OrphanedSession[] {
    const currentSessionId = this.readSession()?.sessionId ?? null;
    const orphans: OrphanedSession[] = [];

    // Snapshot the keys first — modifying localStorage while iterating is unsafe
    const orphanKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(ActivityTrackerService.KEY_EVENTS_PREFIX)) {
        const sessionId = key.slice(ActivityTrackerService.KEY_EVENTS_PREFIX.length);
        if (sessionId !== currentSessionId) {
          orphanKeys.push(key);
        }
      }
    }

    for (const key of orphanKeys) {
      const sessionId = key.slice(ActivityTrackerService.KEY_EVENTS_PREFIX.length);
      try {
        const events = JSON.parse(localStorage.getItem(key) ?? '[]') as ActivityEvent[];
        if (events.length > 0) {
          // Best-effort: recover session metadata from the first event if no meta key exists
          orphans.push({
            sessionId,
            type: 'np-activity', // orphan recovery is only relevant for NP sessions
            userId: 'unknown',
            userName: 'unknown',
            events,
          });
        }
      } catch {
        // Corrupted buffer — skip and remove
      }
      localStorage.removeItem(key);
    }

    return orphans;
  }

  // ── Private helpers ──────────────────────────────────────────────────────

  private readSession(): ActivitySession | null {
    try {
      const raw = localStorage.getItem(ActivityTrackerService.KEY_SESSION);
      return raw ? (JSON.parse(raw) as ActivitySession) : null;
    } catch {
      return null;
    }
  }

  private readEvents(sessionId: string): ActivityEvent[] {
    try {
      return JSON.parse(localStorage.getItem(this.eventsKey(sessionId)) ?? '[]') as ActivityEvent[];
    } catch {
      return [];
    }
  }

  private eventsKey(sessionId: string): string {
    return `${ActivityTrackerService.KEY_EVENTS_PREFIX}${sessionId}`;
  }

  private readActivityLogDetails(storageKey: string): ActivityLogDetail[] {
    try {
      return JSON.parse(localStorage.getItem(storageKey) ?? '[]') as ActivityLogDetail[];
    } catch {
      return [];
    }
  }

  private writeActivityLogDetails(storageKey: string, details: ActivityLogDetail[]): void {
    localStorage.setItem(storageKey, JSON.stringify(details));
  }

  private generateSessionId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
}
